import { ISignatureField, ISignaturePreview } from '../models/ISignatureProfile.interface';

/**
 * A cut-down stand-in for the server-side Scriban renderer, good enough to make the preview pane
 * real while `environment.useSignatureFixtures` is on.
 *
 * It is not a Scriban implementation and does not try to be — only `{{ key }}` substitution, which
 * is all the seeded templates use. What it *does* copy exactly are the three rules from
 * `backend-todo-signatures.md` §7.1 that decide whether output is safe:
 *
 * - every interpolated value is HTML-encoded, so a job title of `<script>alert(1)</script>` renders
 *   as text rather than executing in the preview pane;
 * - the hidden marker span is emitted exactly once, since duplicate-signature suppression keys off
 *   it and a preview that omits it would hide the bug the marker exists to prevent;
 * - a plain-text alternative comes out of the same call, never as an afterthought.
 */

const MARKER_STYLE = 'display:none;font-size:0;line-height:0;mso-hide:all';

function encode(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Values keyed for substitution — the flat, whitelisted model, and nothing else.
 *
 * `designations` used to have its comma baked in here, because the template had no way to add one
 * only when the field was set. Now that the template guards it with `{{#if}}` the separator lives
 * where it is read, and leaving both in place produced `Alex Georgiou, , FCCA`.
 */
function buildContext(fields: ISignatureField[]): Record<string, string> {
  const context: Record<string, string> = {};
  for (const field of fields) {
    context[field.key] = (field.value ?? '').trim();
  }
  return context;
}

/**
 * `{{#if key}} … {{/if}}` — the block survives only when the field has a value.
 *
 * Mirrors the server renderer (`BC_Corporate/Services/Signatures/SignatureRenderer.cs`), including
 * being deliberately non-nested. Conditionals resolve before tokens, so a value that happens to
 * contain `{{/if}}` cannot close a block early.
 */
function resolveConditionals(source: string, context: Record<string, string>): string {
  return source.replace(
    /\{\{#if\s+([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}([\s\S]*?)\{\{\/if\s*\}\}/g,
    (_match, key: string, body: string) => (context[key]?.trim() ? body : ''),
  );
}

function substitute(source: string, context: Record<string, string>, escapeValues: boolean): string {
  return resolveConditionals(source, context)
    .replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_match, key: string) => {
      const value = context[key] ?? '';
      return escapeValues ? encode(value) : value;
    });
}

/**
 * Drops a row whose cells all came out empty.
 *
 * The templates now guard every optional field with `{{#if}}`, so a blank field takes its label and
 * separator with it — but the row it sat on remains, and an empty `<tr>` is a blank line in the
 * middle of the signature. The server renderer removes these with the same rule.
 *
 * What used to live here was a set of regexes unpicking dangling "D" labels and doubled separators
 * after the fact. That was papering over templates that did not guard their fields; the guards are
 * the fix, and this is what is left.
 */
function tidy(html: string): string {
  return html
    // A separator at the very start of a cell has nothing left to separate. Templates attach the
    // separator to the second of a pair — "D 2245 · M 9941" guards each leg — so a person with a
    // mobile but no direct line would otherwise open that cell with " · ".
    .replace(/(<t[dh]\b[^>]*>)(?:\s|&nbsp;)*(?:(?:&middot;|·|\|)(?:\s|&nbsp;)*)+/gi, '$1')
    // An empty cell that carries a border or a background IS the content — it is the divider rule
    // under the name, or a spacer. Dropping those rows deleted the horizontal line from every
    // signature, and nothing on screen said so.
    .replace(
      /<tr\b[^>]*>\s*(?:<t[dh]\b(?![^>]*(?:border|background))[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*<\/t[dh]>\s*)+<\/tr>/gi,
      '',
    );
}

/** A guarded field that vanished leaves the line it was on; collapse the gap. */
function tidyPlain(text: string): string {
  return text
    .replace(/^[ \t]*(?:[|·][ \t]*)+/gm, '')   // Same rule as tidy(), per line.
    .replace(/^[ \t]+$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export interface RenderInput {
  bodyHtml: string;
  bodyPlainText: string;
  fields: ISignatureField[];
  /** Written into the marker span so the transport side can identify what it is looking at. */
  profileId: string;
  templateVersion: number;
  /**
   * Values a template can read that are not the profile's own fields: the entity name, the logo
   * and sign-off images, and a ready-made icon URL per social platform.
   *
   * The icon URLs are composed by the caller rather than in the template because the template
   * language has no way to join two values — deliberately. Guarding on the composed URL is also
   * what stops a firm with no icon folder emitting `src="/linkedIn.png"`, which resolves against
   * the recipient's mail host and 404s.
   */
  extras?: Record<string, string | null | undefined>;
}

export function renderSignature(input: RenderInput): ISignaturePreview {
  const context = buildContext(input.fields);

  for (const [key, value] of Object.entries(input.extras ?? {})) {
    context[key] = (value ?? '').trim();
  }

  const marker = `<span id="bct-sig" data-p="${encode(input.profileId)}" `
    + `data-v="${input.templateVersion}" style="${MARKER_STYLE}"></span>`;

  const html = tidy(substitute(input.bodyHtml, context, true)) + marker;
  const plainText = tidyPlain(substitute(input.bodyPlainText, context, false));

  return { html, plainText };
}
